import React from "react";
import { toast } from "sonner";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

interface RegisterFormProps {
  name: string;
  contact: string;
  role: string;
  password: string;
  confirmPassword: string;
  affiliation: string;
  errors: {
    name: string;
    contact: string;
    role: string;
    password: string;
    confirmPassword: string;
    secretCode?: string;
  };
  setName: (value: string) => void;
  setContact: (value: string) => void;
  setRole: (value: string) => void;
  setPassword: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  setAffiliation: (value: string) => void;
  handleFinalRegister: () => void;
  validateForm: () => boolean;
  onSubmit: () => void;
  submitting?: boolean;
}

const inputClassName =
  "h-12 rounded-lg border-slate-200 bg-white px-4 text-[15px] text-slate-900 placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-[#fb5120] focus-visible:ring-offset-0";

const RegisterForm: React.FC<RegisterFormProps> = ({
  name,
  contact,
  password,
  confirmPassword,
  errors,
  setName,
  setContact,
  setPassword,
  setConfirmPassword,
  validateForm,
  onSubmit,
  submitting = false,
}) => {
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="register-name" className="text-sm font-semibold text-slate-900">
            이름
          </Label>
          <Input
            id="register-name"
            className={inputClassName}
            placeholder="이름을 입력해 주세요."
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
          />
          {errors.name && <p className="text-sm text-rose-600">{errors.name}</p>}
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="register-contact"
            className="text-sm font-semibold text-slate-900"
          >
            연락처
          </Label>
          <Input
            id="register-contact"
            className={inputClassName}
            placeholder="01012345678"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            autoComplete="tel"
            inputMode="numeric"
          />
          {errors.contact && (
            <p className="text-sm text-rose-600">{errors.contact}</p>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
        비밀번호는 소문자, 숫자, 특수문자를 포함한 8자 이상으로 입력해 주세요.
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label
            htmlFor="register-password"
            className="text-sm font-semibold text-slate-900"
          >
            비밀번호
          </Label>
          <Input
            id="register-password"
            type="password"
            className={inputClassName}
            placeholder="비밀번호를 입력해 주세요."
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
          {errors.password && (
            <p className="text-sm text-rose-600">{errors.password}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="register-password-confirm"
            className="text-sm font-semibold text-slate-900"
          >
            비밀번호 확인
          </Label>
          <Input
            id="register-password-confirm"
            type="password"
            className={inputClassName}
            placeholder="비밀번호를 다시 입력해 주세요."
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
          />
          {errors.confirmPassword && (
            <p className="text-sm text-rose-600">{errors.confirmPassword}</p>
          )}
        </div>
      </div>

      <Button
        type="button"
        onClick={async () => {
          const ok = validateForm();
          if (!ok) {
            toast.error("필수 항목을 올바르게 입력해 주세요.");
            return;
          }
          await onSubmit();
        }}
        className="h-12 w-full rounded-lg bg-[#fb5120] font-semibold text-white hover:bg-[#e5481b] disabled:bg-slate-200 disabled:text-slate-500"
        disabled={submitting}
      >
        {submitting ? "처리 중..." : "회원가입 완료"}
      </Button>
    </div>
  );
};

export default RegisterForm;
